// Keyboard Shortcuts Manager
export class KeyboardManager {
  constructor(stateManager, photoManager, uiManager, modalManager, sliderManager) {
    this.stateManager = stateManager;
    this.editorState = stateManager.editorState; // Direct reference to reactive state
    this.photoManager = photoManager;
    this.uiManager = uiManager;
    this.modalManager = modalManager;
    this.sliderManager = sliderManager;
  }

  /**
   * Setup keyboard event listeners
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });
  }

  /**
   * Handle keydown events
   */
  handleKeydown(e) {
    const state = this.editorState.get();
    
    // Guard against undefined key
    if (!e.key) return;
    
    const key = e.key.toLowerCase();
    const isMeta = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;
    
    // Don't trigger shortcuts when typing in text input fields (but allow on range sliders)
    const isTextInput = (e.target.tagName === 'INPUT' && e.target.type !== 'range') || e.target.tagName === 'TEXTAREA';
    if (isTextInput) {
      return;
    }
    
    // On Edit page with sliders, left/right always navigate photos (prevent slider default)
    const isSlider = e.target.tagName === 'INPUT' && e.target.type === 'range';
    if (isSlider && state.currentPage === 'edit' && (key === 'arrowleft' || key === 'arrowright')) {
      e.preventDefault(); // Prevent slider from handling left/right
      if (key === 'arrowleft') {
        if (isShift) {
          this.photoManager.navigatePreviousWithSelection();
        } else {
          this.photoManager.navigatePrevious();
        }
      } else if (key === 'arrowright') {
        if (isShift) {
          this.photoManager.navigateNextWithSelection();
        } else {
          this.photoManager.navigateNext();
        }
      }
      return;
    }
    
    // Page Navigation
    if (key === 'g' && !isMeta) {
      e.preventDefault();
      this.uiManager.showPage('photos');
      this.stateManager.updateUrl('photos');
      this.stateManager.saveState();
    } else if (key === 'd' && !isMeta) {
      e.preventDefault();
      this.uiManager.showPage('edit');
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      this.stateManager.updateUrl('edit', activePhoto);
      this.stateManager.saveState();
    } else if (key === 's' && !isMeta) {
      e.preventDefault();
      this.uiManager.showPage('export');
      this.stateManager.updateUrl('export');
      this.stateManager.saveState();
    }
    
    // Photo Navigation on Photos Page
    else if (this.photoManager.getPhotos().length > 0 && state.currentPage === 'photos') {
      if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key)) {
        e.preventDefault();
        const direction = key.replace('arrow', '');
        const navigated = this.photoManager.navigatePhotosGrid(direction, isShift);
        if (navigated) {
          // Get updated state after navigation
          const updatedState = this.editorState.get();
          const activePhoto = this.photoManager.getPhotoById(updatedState.activePhotoId);
          this.uiManager.renderPhotos(
            this.photoManager.getPhotos(),
            updatedState.selectedPhotoIds,
            activePhoto
          );
        }
      }
    }
    
    // Photo Navigation and Slider Adjustment on Edit Page (Lightroom-style)
    else if (state.currentPage === 'edit' && state.activePhotoId) {
      // Left/Right: Always navigate between photos
      if (key === 'arrowleft' && !isMeta) {
        e.preventDefault();
        if (isShift) {
          // Shift+Left: Add previous photo to selection
          this.photoManager.navigatePreviousWithSelection();
        } else {
          this.photoManager.navigatePrevious();
        }
      } else if (key === 'arrowright' && !isMeta) {
        e.preventDefault();
        if (isShift) {
          // Shift+Right: Add next photo to selection
          this.photoManager.navigateNextWithSelection();
        } else {
          this.photoManager.navigateNext();
        }
      }
      // Up/Down: Adjust focused slider value
      else if (key === 'arrowup' && !isMeta) {
        this.adjustFocusedSlider(0.5, e);  // Increase by 0.5
      } else if (key === 'arrowdown' && !isMeta) {
        this.adjustFocusedSlider(-0.5, e);  // Decrease by 0.5
      }
    }
    
    // Full Screen Toggle
    else if (key === 'f' && !isMeta) {
      e.preventDefault();
      this.toggleFullscreen();
    }
    
    // Tab to toggle sidebar
    else if (key === 'tab' && !isMeta && state.currentPage === 'photos') {
      e.preventDefault();
      this.toggleSidebar();
    }
    
    // Escape key
    else if (key === 'escape') {
      // PREVIEW BAR CODE REMOVED - Now using PreviewBox
      // // Close preview bar first (if open)
      // if (this.sliderManager.previewBar) {
      //   this.sliderManager.closePreviewBar();
      // } else
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        this.modalManager.hideAll();
      }
    }
    
    // Select All
    else if (key === 'a' && isMeta && state.currentPage === 'photos') {
      e.preventDefault();
      this.photoManager.selectAll();
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      this.uiManager.renderPhotos(
        this.photoManager.getPhotos(),
        state.selectedPhotoIds,
        activePhoto
      );
    }
    
    // Deselect All
    else if (key === 'd' && isMeta) {
      e.preventDefault();
      this.photoManager.deselectAll();
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      this.uiManager.renderPhotos(
        this.photoManager.getPhotos(),
        state.selectedPhotoIds,
        activePhoto
      );
    }
    
    // Delete selected photos
    else if ((key === 'delete' || key === 'backspace') && state.currentPage === 'photos' && state.selectedPhotoIds.size > 0) {
      e.preventDefault();
      this.deleteSelectedPhotos();
    }
    
    // Spacebar for quick view
    else if (key === ' ' && state.currentPage === 'photos' && state.selectedPhotoIds.size === 1) {
      e.preventDefault();
      const photoId = Array.from(state.selectedPhotoIds)[0];
      const photo = this.photoManager.getPhotos().find(p => p.id === photoId);
      if (photo) this.photoManager.editPhoto(photo);
    }
    
    // Number keys for sliders on Edit page
    else if (state.currentPage === 'edit' && !isMeta && /^[1-8]$/.test(key)) {
      e.preventDefault();
      this.focusSlider(parseInt(key) - 1);
    }
    
    // 0 to reset all sliders
    else if (key === '0' && !isMeta && state.currentPage === 'edit') {
      e.preventDefault();
      this.resetAllSliders();
    }
    
    // Plus/Minus to adjust focused slider
    else if ((key === '+' || key === '=' || key === '-') && state.currentPage === 'edit') {
      this.adjustFocusedSlider(key === '-' ? -5 : 5, e);
    }
    
    // L for "Lights Out" mode
    else if (key === 'l' && !isMeta) {
      e.preventDefault();
      document.body.classList.toggle('lights-out');
    }
    
    // Z for zoom toggle on Edit page
    else if (key === 'z' && !isMeta && state.currentPage === 'edit') {
      e.preventDefault();
      this.toggleZoom();
    }
    
    // Question mark for help
    else if ((key === '?' || (isShift && key === '/')) && !isMeta) {
      e.preventDefault();
      this.showKeyboardShortcuts();
    }
  }

  /**
   * Toggle fullscreen
   */
  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar() {
    const sidebar = document.querySelector('.md\\:w-64');
    if (sidebar) {
      sidebar.classList.toggle('hidden');
    }
  }

  /**
   * Delete selected photos with confirmation
   */
  async deleteSelectedPhotos() {
    const state = this.editorState.get();
    const confirmed = await this.modalManager.confirm(
      `Delete ${state.selectedPhotoIds.size} photo(s)?`
    );
    
    if (confirmed) {
      await this.photoManager.deleteSelected();
      const updatedState = this.editorState.get();
      const activePhoto = this.photoManager.getPhotoById(updatedState.activePhotoId);
      this.uiManager.renderPhotos(
        this.photoManager.getPhotos(),
        updatedState.selectedPhotoIds,
        activePhoto
      );
    }
  }

  /**
   * Focus a slider by index
   */
  focusSlider(index) {
    if (this.sliderManager) {
      this.sliderManager.focusSlider(index);
    }
  }

  /**
   * Reset all sliders to default
   */
  resetAllSliders() {
    const state = this.editorState.get();
    if (this.sliderManager && state.activePhotoId) {
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      if (activePhoto) {
        this.sliderManager.resetAllSliders(activePhoto);
      }
    }
  }

  /**
   * Adjust the focused slider
   */
  adjustFocusedSlider(step, event) {
    if (this.sliderManager) {
      event.preventDefault();
      this.sliderManager.adjustFocusedSlider(step);
    }
  }

  /**
   * Toggle zoom on Edit page
   */
  toggleZoom() {
    const img = document.querySelector('#edit-page img');
    if (img) {
      if (img.classList.contains('object-contain')) {
        img.classList.remove('object-contain');
        img.classList.add('object-cover');
      } else {
        img.classList.remove('object-cover');
        img.classList.add('object-contain');
      }
    }
  }

  /**
   * Show keyboard shortcuts help modal
   */
  showKeyboardShortcuts() {
    const shortcuts = [
      { category: 'Navigation', items: [
        { key: 'G', description: 'Go to Photos page' },
        { key: 'D', description: 'Go to Edit page' },
        { key: 'S', description: 'Go to Export page' },
        { key: 'Arrow Keys', description: 'Navigate photos (Photos page) or edit previous/next (Edit page)' },
        { key: 'Shift + Arrows', description: 'Expand selection while navigating' }
      ]},
      { category: 'Selection', items: [
        { key: 'Cmd/Ctrl + A', description: 'Select all photos' },
        { key: 'Cmd/Ctrl + D', description: 'Deselect all photos' },
        { key: 'Cmd/Ctrl + Click', description: 'Toggle photo selection' },
        { key: 'Shift + Click', description: 'Select range of photos' },
        { key: 'Space', description: 'Quick view selected photo' }
      ]},
      { category: 'Editing', items: [
        { key: '1-8', description: 'Focus slider 1-8' },
        { key: '0', description: 'Reset all sliders to default' },
        { key: '+/-', description: 'Adjust focused slider' },
        { key: 'Z', description: 'Toggle zoom (fit/fill)' }
      ]},
      { category: 'View', items: [
        { key: 'F', description: 'Toggle fullscreen' },
        { key: 'L', description: 'Lights out mode' },
        { key: 'Tab', description: 'Toggle sidebar (Photos page)' }
      ]},
      { category: 'Other', items: [
        { key: 'Delete/Backspace', description: 'Delete selected photos' },
        { key: 'Esc', description: 'Close modals / Exit fullscreen' },
        { key: '?', description: 'Show this help' }
      ]}
    ];

    let html = '<div class="space-y-6">';
    shortcuts.forEach(section => {
      html += `
        <div>
          <h3 class="text-lg font-semibold text-white mb-3">${section.category}</h3>
          <div class="space-y-2">
      `;
      section.items.forEach(item => {
        html += `
          <div class="flex justify-between items-center">
            <span class="text-gray-300">${item.description}</span>
            <kbd class="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 border border-gray-300 rounded">${item.key}</kbd>
          </div>
        `;
      });
      html += '</div></div>';
    });
    html += '</div>';

    // Create or update help modal
    let modal = document.getElementById('keyboard-shortcuts-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'keyboard-shortcuts-modal';
      modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 hidden';
      modal.innerHTML = `
        <div class="bg-gray-800 rounded-lg p-6 max-w-2xl max-h-[80vh] overflow-y-auto">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold text-white">Keyboard Shortcuts</h2>
            <button class="text-gray-400 hover:text-white" onclick="this.closest('#keyboard-shortcuts-modal').classList.add('hidden')">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div id="shortcuts-content"></div>
        </div>
      `;
      document.body.appendChild(modal);
    }
    
    document.getElementById('shortcuts-content').innerHTML = html;
    modal.classList.remove('hidden');
  }
}

